import type { Metaobject } from "@repo/shopify/types/generated/storefront.types";
import type { CSSProperties } from "react";
import React, { useMemo, useRef } from "react";

// TODO: CDN 링크로 교체 후 CORS 확인
import sceneSrc from "@assets/goretex/goretex_scene.mp4";
import posterSrc from "@assets/goretex/goretex_scene_poster.jpg";

import Link from "@atoms/Link";

import HomeCard from "@molecules/HomeCard";

import usePersonCutout from "@hooks/usePersonCutout";
import useThermalLogo from "@hooks/useThermalLogo";
import useTiltParallax from "@hooks/useTiltParallax";

interface IProps {
  node: Metaobject;
}

// offset: 최대 이동량 (자기 크기 대비 %)
// rotate: 최대 회전 각도 (deg)
const LAYER_MOTION = {
  logo: { offset: 5, rotate: 4 },
  background: { offset: 2.5, rotate: -6 },
};
// 회전 원근감 (px)
const PERSPECTIVE = 800;
// 영상 크기
const VIDEO_SCALE = 1.4;

const HAS_WEBGL = Boolean(document.createElement("canvas").getContext("webgl"));

const layerStyle = ({ offset, rotate }: { offset: number; rotate: number }, scale = 1): CSSProperties => ({
  transform: [
    `perspective(${PERSPECTIVE}px)`,
    `translate(calc(var(--tilt-x, 0) * ${offset}%), calc(var(--tilt-y, 0) * ${offset}%))`,
    `rotateX(calc(var(--tilt-y, 0) * ${-rotate}deg))`,
    `rotateY(calc(var(--tilt-x, 0) * ${rotate}deg))`,
    `scale(${scale})`,
  ].join(" "),
});

export default function GoreTexCard({ node }: IProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const backgroundRef = useRef<HTMLCanvasElement>(null);
  const personRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLCanvasElement>(null);

  const data = useMemo(() => {
    const link = node?.fields.find((item) => item.key === "link")?.value || "/";
    const title = node?.fields.find((item) => item.key === "title")?.value || "";
    const description = node?.fields.find((item) => item.key === "description")?.value || "";

    return {
      link,
      title,
      description,
    };
  }, [node]);

  // 틸트
  const { needsPermission, requestPermission } = useTiltParallax(containerRef);
  // 인물 누끼, 로고 앞뒤
  const { autoplayBlocked } = usePersonCutout({ containerRef, videoRef, canvasRef: personRef, backgroundRef });

  useThermalLogo({ containerRef, videoRef, canvasRef: logoRef });
  // The footage still contains the person: its cutout must use the exact same transform.
  const footageStyle = layerStyle(LAYER_MOTION.background, VIDEO_SCALE);

  if (!HAS_WEBGL) return <HomeCard node={node} />;

  return (
    <Link className="flex! flex-col gap-2 self-start" href={data.link}>
      <div className="relative w-full overflow-hidden bg-black" ref={containerRef} style={{ aspectRatio: "4 / 5" }}>
        {/* 배경 */}
        <div className="absolute inset-0 z-0 overflow-hidden will-change-transform" style={footageStyle}>
          <canvas aria-hidden="true" className="absolute inset-0 size-full" ref={backgroundRef} />
          <video className="hidden" loop muted playsInline poster={posterSrc} preload="auto" ref={videoRef} src={sceneSrc} />
        </div>

        {/* 로고 */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center mix-blend-screen will-change-transform"
          style={{ ...layerStyle(LAYER_MOTION.logo), zIndex: "var(--logo-z, 30)" }}
        >
          <div aria-label="GORE-TEX" className="relative aspect-square w-[84%] opacity-[0.88] blur-[0.8px]" role="img">
            <canvas aria-hidden="true" className="size-full" ref={logoRef} />
          </div>
        </div>

        {/* 인물 */}
        <canvas className="absolute inset-0 z-20 size-full will-change-transform" ref={personRef} style={footageStyle} />

        {needsPermission || autoplayBlocked ? (
          <button
            className="absolute inset-0 z-40 flex items-end justify-center pb-6 text-white"
            onClick={(event) => {
              event.preventDefault();
              if (autoplayBlocked) void videoRef.current?.play();
              if (needsPermission) void requestPermission();
            }}
            type="button"
          >
            {autoplayBlocked ? "Tap to play" : "Tap to tilt"}
          </button>
        ) : null}
      </div>

      <div className="flex flex-col gap-0.5">
        <span>{data.title}</span>
        <span className="font-normal">{data.description}</span>
      </div>
    </Link>
  );
}
